import { extendTheme } from '@chakra-ui/react';

// Haven brand colors from prototype
const havenColors = {
  // Primary Colors
  'navy-blue': '#4D6C8E',
  'sage': '#F7F5F0',
  'warm-cream': '#D6B5A0',
  'light-sage': '#BCC4B8',
  'medium-sage': '#A8B3A5',
  'dark-sage': '#95A592',
  'charcoal': '#3A3A3C',
  'warm-gray': '#8E8E93',
  'light-gray': '#C7C7CC',
  'off-white': '#F2F2F7',
  'carbon': '#1E1E1E',
  'sand': '#D59462',
  
  // Prototype specific colors
  'header-bg': '#02303C',
  'grello': '#CDDC2B',
  'tagline-text': '#5D9A9B',
  'background': '#EFF2F2',
  
  // Secondary Colors
  'fog': '#B8C4C4',
  'rain': '#5A7FA4',
  'turquoise': '#4A9B9B',
  'sage-green': '#8FA68F',
  'sunset': '#E67E22',
  'periwinkle': '#A8B3D9',
  
  // Tertiary Accent Colors
  'soft-citrus': '#F4E4BC',
  'vibrant-peach': '#FF8C42',
  'orchid': '#C77DFF',
  'coastal-green': '#7FB069',
  'amber': '#FFB627',
  'plum': '#8B5A96',
};

// Tailwind-equivalent font sizes (16px base): ensure rem resolves correctly
const tailwindFontSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  md: '1rem',       // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem',    // 48px
  '6xl': '3.75rem', // 60px
  '7xl': '4.5rem',  // 72px
  '8xl': '6rem',    // 96px
  '9xl': '8rem',    // 128px
};

const theme = extendTheme({
  styles: {
    global: {
      html: {
        fontSize: '16px', // ensure rem = Tailwind/prototype scale
      },
    },
  },
  colors: {
    ...havenColors,
    // Override Chakra defaults with Haven colors
    gray: {
      50: '#F7F5F0', // sage
      100: '#EFF2F2', // background
      200: '#BCC4B8', // light-sage
      300: '#A8B3A5', // medium-sage
      400: '#95A592', // dark-sage
      500: '#8E8E93', // warm-gray
      600: '#5A7FA4', // rain
      700: '#4D6C8E', // navy-blue
      800: '#3A3A3C', // charcoal
      900: '#1E1E1E', // carbon
    },
    blue: {
      50: '#EFF2F2',
      100: '#BCC4B8',
      200: '#A8B3A5',
      300: '#95A592',
      400: '#8E8E93',
      500: '#5A7FA4', // rain
      600: '#4D6C8E', // navy-blue
      700: '#02303C', // header-bg
      800: '#1E1E1E',
      900: '#000000',
    },
    green: {
      50: '#F7F5F0',
      100: '#EFF2F2',
      200: '#BCC4B8',
      300: '#A8B3A5',
      400: '#95A592',
      500: '#8FA68F', // sage-green
      600: '#7FB069', // coastal-green
      700: '#4A9B9B', // turquoise
      800: '#02303C',
      900: '#1E1E1E',
    },
    yellow: {
      50: '#F4E4BC', // soft-citrus
      100: '#FFB627', // amber
      200: '#CDDC2B', // grello
      300: '#FF8C42', // vibrant-peach
      400: '#E67E22', // sunset
      500: '#D59462', // sand
      600: '#B8C4C4', // fog
      700: '#8FA68F', // sage-green
      800: '#4A9B9B', // turquoise
      900: '#02303C', // header-bg
    },
  },
  fonts: {
    heading: 'Volte, Poppins, sans-serif',
    body: 'Neue Haas Grotesk, Poppins, sans-serif',
    mono: 'Menlo, monospace',
  },
  fontSizes: tailwindFontSizes,
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    normal: 'normal',
    none: 1,
    shorter: 1.25,
    short: 1.375,
    base: 1.5,
    tall: 1.625,
    taller: '2',
    '3': '.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '7': '1.75rem',
    '8': '2rem',
    '9': '2.25rem',
    '10': '2.5rem',
  },
  letterSpacings: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  breakpoints: {
    base: '0em',
    sm: '30em',
    md: '48em',
    lg: '62em',
    xl: '80em',
    '2xl': '96em',
  },
  space: {
    px: '1px',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
  },
  sizes: {
    ...Object.fromEntries(
      Object.entries({
        px: '1px',
        0.5: '0.125rem',
        1: '0.25rem',
        1.5: '0.375rem',
        2: '0.5rem',
        2.5: '0.625rem',
        3: '0.75rem',
        3.5: '0.875rem',
        4: '1rem',
        5: '1.25rem',
        6: '1.5rem',
        7: '1.75rem',
        8: '2rem',
        9: '2.25rem',
        10: '2.5rem',
        12: '3rem',
        14: '3.5rem',
        16: '4rem',
        20: '5rem',
        24: '6rem',
        28: '7rem',
        32: '8rem',
        36: '9rem',
        40: '10rem',
        44: '11rem',
        48: '12rem',
        52: '13rem',
        56: '14rem',
        60: '15rem',
        64: '16rem',
        72: '18rem',
        80: '20rem',
        96: '24rem',
      }).map(([key, value]) => [key, value])
    ),
    xs: '20rem',
    sm: '24rem',
    md: '28rem',
    lg: '32rem',
    xl: '36rem',
    '2xl': '42rem',
    '3xl': '48rem',
    '4xl': '56rem',
    '5xl': '64rem',
    '6xl': '72rem',
    '7xl': '80rem',
    full: '100%',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
  },
  radii: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
  shadows: {
    xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    outline: '0 0 0 3px rgba(66, 153, 225, 0.6)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
    'dark-lg': 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px, rgba(0, 0, 0, 0.1) 0px 5px 5px',
  },
  zIndices: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
  components: {
    Heading: {
      baseStyle: {
        fontFamily: 'heading',
      },
      sizes: {
        xs: { fontSize: 'xs', lineHeight: '1.25' },
        sm: { fontSize: 'sm', lineHeight: '1.25' },
        md: { fontSize: 'md', lineHeight: '1.25' },
        lg: { fontSize: 'lg', lineHeight: '1.25' },
        xl: { fontSize: 'xl', lineHeight: '1.2' },
        '2xl': { fontSize: '2xl', lineHeight: '1.2' },
        '3xl': { fontSize: '3xl', lineHeight: '1.2' },
        '4xl': { fontSize: '4xl', lineHeight: '1.1' },
        '5xl': { fontSize: '5xl', lineHeight: '1.1' },
        '6xl': { fontSize: '6xl', lineHeight: '1' },
        '7xl': { fontSize: '7xl', lineHeight: '1' },
      },
    },
    Button: {
      baseStyle: {
        fontWeight: 'medium',
        borderRadius: 'lg',
        _focus: {
          boxShadow: 'outline',
        },
      },
      variants: {
        solid: {
          bg: 'blue.600',
          color: 'white',
          _hover: {
            bg: 'blue.700',
            _disabled: {
              bg: 'blue.600',
            },
          },
        },
        outline: {
          border: '1px solid',
          borderColor: 'blue.600',
          color: 'blue.600',
          _hover: {
            bg: 'blue.50',
          },
        },
        ghost: {
          color: 'blue.600',
          _hover: {
            bg: 'blue.50',
          },
        },
      },
    },
  },
});

export default theme;
